import type { SelectChangeEvent } from "@mui/material";
import type { ReactNode } from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  NavLink,
  useLocation,
  useMatch,
  useNavigate,
  useResolvedPath,
} from "react-router";

import { SuccessIcon } from "@citrine/atoms/icons";
import { Item } from "@citrine/atoms/item";
import { TruncatedText } from "@citrine/atoms/truncated-text";
import { WarningIcon } from "@citrine/atoms/warning-icon";
import type { TranslationID } from "@citrine/lang/utils";
import type { ColorName } from "@citrine/theme";
import type { DataFullstoryId } from "@citrine/types/full-story";

import type { Tab, TabLabelProps } from "./tab-panel";
import { TabPanel } from "./tab-panel";
import * as sx from "./tabs.sx";

export type TabsProps<Value extends string = string> = {
  id?: string;
  tabs: Tab<Value>[];
  routed?: boolean;
  className?: string;
  children?: React.ReactNode;
  defaultValue?: Value | false;
  panelClass?: string;
  headerChildren?: ReactNode;
  headerId?: TranslationID;
  tooltipTranslationId?: TranslationID;
  tooltipHeaderTranslationId?: TranslationID;
  onTabClick?: (value: Value, currentTab: string) => boolean;
  onSelection?: (value: Value | false) => void;
  "data-testid"?: string;
  scrollable?: boolean;
  size?: sx.Size;
  variant?: sx.Variant;
} & (
  | {
      variant: "select";
      color?: undefined;
      progress?: undefined;
      size?: undefined;
      "data-fullstoryid"?: DataFullstoryId;
    }
  | {
      variant?: "buttons";
      color?: ColorName;
      progress?: boolean;
      "data-fullstoryid"?: undefined;
    }
);

export interface TabsRef<Value extends string = string> {
  select: (value: Value | false) => void;
  getSelectedTab: () => Value | false;
}

interface StyleProps {
  color: ColorName;
  hasHeader?: boolean;
  selection?: string | false;
  skew?: boolean;
  spacing?: number;
  size?: TabsProps["size"];
}

function TabLabel({
  error,
  errorTooltip,
  icon,
  progress,
  success,
  warning,
  warningTooltip,
  ...props
}: TabLabelProps) {
  return (
    <sx.TabLabel>
      {error || warning ? (
        <WarningIcon
          severity={error ? "error" : "warning"}
          title={errorTooltip ?? warningTooltip ?? ""}
        />
      ) : null}
      {success ? <SuccessIcon fontSize="small" /> : null}
      {icon || null}
      <TruncatedText>
        <Item {...props} />
      </TruncatedText>
    </sx.TabLabel>
  );
}

function TabsImpl<Value extends string = string>(
  {
    id,
    headerId,
    tabs,
    className,
    routed,
    children,
    defaultValue,
    headerChildren,
    progress,
    color: colorProp,
    panelClass,
    variant = "buttons",
    onSelection,
    onTabClick,
    "data-testid": testId,
    tooltipTranslationId,
    tooltipHeaderTranslationId,
    scrollable = true,
    size = "large",
    "data-fullstoryid": selectFullStoryId,
  }: TabsProps<Value>,
  ref: React.ForwardedRef<TabsRef<Value>>,
) {
  const enabledTabs = useMemo(
    () => tabs.filter((tab) => !tab.disabled).map((tab) => tab.value),
    [tabs],
  );

  const isSelectable = useCallback(
    (tabValue?: string | false): tabValue is Value =>
      Boolean(tabValue && (enabledTabs as unknown[]).includes(tabValue)),
    [enabledTabs],
  );

  const firstSelectable = enabledTabs[0];

  // do not allow disabled tabs to be selected, even programmatically
  const getSelectableTab = useCallback(
    (tabValue?: string | false): Value | false =>
      tabValue === false
        ? false
        : isSelectable(tabValue)
          ? tabValue
          : (firstSelectable ?? false),
    [isSelectable, firstSelectable],
  );

  // preserve query params on route change
  const { search } = useLocation();
  // get the base route where these tabs are mounted
  const { pathname: routedPath } = useResolvedPath("../:routedValue");
  const routedMatch = useMatch(routedPath);
  const routedValue = routedMatch?.params.routedValue;

  const [selection, setSelection] = useState<Value | false>(() =>
    getSelectableTab((routed ? routedValue : undefined) || defaultValue),
  );

  const navigate = useNavigate();
  const select = useCallback(
    (value: Value | false) => {
      const tabValue = getSelectableTab(value);
      setSelection(tabValue);
      if (tabValue) {
        onSelection?.(tabValue);
      }
      // if tabs are routed, update the url
      if (routed && tabValue !== routedValue) {
        navigate(`../${tabValue || ""}${search}`, { replace: true });
      }
    },
    [getSelectableTab, navigate, onSelection, routed, routedValue, search],
  );

  useEffect(() => {
    if (routed) {
      if (!isSelectable(routedValue)) {
        // user navigated to the root or invalid URL; update the route
        select(selection);
      } else if (routedValue !== selection && isSelectable(routedValue)) {
        // user clicked a link somewhere on the page; respect the new route
        select(routedValue);
      }
    }
  }, [enabledTabs, isSelectable, routed, routedValue, select, selection]);

  const getSelectedTab = useCallback(() => selection, [selection]);
  useImperativeHandle(ref, () => ({ select, getSelectedTab }), [
    select,
    getSelectedTab,
  ]);

  const onButtonClick = useCallback(
    (event: React.ChangeEvent, value: Value) => {
      event.preventDefault();
      const shouldContinue = onTabClick?.(value, selection || "") ?? true;
      if (shouldContinue) {
        select(value);
      }
    },
    [onTabClick, selection, select],
  );

  const selectedIndex = tabs.findIndex(({ value }) => value === selection);

  const color = tabs[selectedIndex]?.color || colorProp || "primary";

  const sharedProps: StyleProps = {
    color,
    size,
  };
  const styleProps: StyleProps = progress
    ? {
        ...sharedProps,
        skew: true,
        spacing: 0.375 /* 3px */,
      }
    : sharedProps;

  const onSelectChange = useCallback(
    (e: SelectChangeEvent<Value>) => {
      select(e.target.value as Value);
    },
    [select],
  );

  return (
    <sx.Wrapper
      data-testid={testId}
      className={className}
      size={size}
      variant={variant}
    >
      {variant === "select" ? (
        <sx.Select
          color={color}
          data-testid="tabs-select"
          data-fullstoryid={selectFullStoryId}
          id={id}
          value={selection || ""}
          options={tabs}
          onChange={onSelectChange}
        />
      ) : (
        <sx.HeaderContainer>
          {headerId ? (
            <sx.Header
              translationId={headerId}
              tooltipTranslationId={tooltipTranslationId}
              tooltipHeaderTranslationId={tooltipHeaderTranslationId}
            >
              {headerChildren}
            </sx.Header>
          ) : null}
          <sx.Scrollable size={size}>
            <sx.Tabs
              {...styleProps}
              hasHeader={Boolean(headerId)}
              id={id}
              selection={selection}
              value={selection}
              variant={scrollable ? "scrollable" : "standard"}
              onChange={onButtonClick}
            >
              {tabs.map((tab: Tab, index: number) => {
                const visited = index < selectedIndex;
                return (
                  <sx.NavTab
                    {...styleProps}
                    to={
                      routed && !tab.disabled
                        ? { pathname: `../${tab.value}`, search }
                        : undefined
                    }
                    component={routed && !tab.disabled ? NavLink : undefined}
                    key={tab.value}
                    data-testid={tab["data-testid"] ?? tab.value}
                    data-fullstoryid={tab["data-fullstoryid"]}
                    disabled={tab.disabled}
                    disableRipple
                    label={
                      <TabLabel {...tab} progress={progress} size={size} />
                    }
                    value={tab.value}
                    className={progress && visited ? "visited" : undefined}
                  />
                );
              })}
            </sx.Tabs>
          </sx.Scrollable>
          {children ? <div>{children}</div> : null}
        </sx.HeaderContainer>
      )}
      {tabs.map((tab: Tab) => (
        <TabPanel
          key={tab.value}
          tab={tab}
          active={tab.value === selection}
          className={panelClass}
        />
      ))}
    </sx.Wrapper>
  );
}

export const Tabs = forwardRef(TabsImpl) as <Value extends string = string>(
  props: TabsProps<Value> & { ref?: React.ForwardedRef<TabsRef<Value>> },
) => ReturnType<typeof TabsImpl>;
